import type { HeaderBiddingInterface, PendingHbAuction } from './header-bidding/types'

export type BannerEventType =
    | 'closed'
    | 'destroyed'
    | 'rewarded'
    | 'empty'
    | 'empty-by-timeout'
    | 'ready'
    | 'request'
    | 'rendered'
    | 'showing'
    | 'show_not_called_after_ready'
    | 'viewable'
    | 'timer'
    | 'clicked'
    | 'interaction_suspected'
    | 'pod_counter'
    | 'unexpected_behavior'
    | 'validation_error'

export type BannerEventListenerArgs = [
    type: BannerEventType,
    callback: (
        event: Event & {
            detail?: {
                secondsLeft?: number
                reason?: string
                adPosition?: number
                totalAds?: number
            }
        },
    ) => void,
    options?: AddEventListenerOptions | boolean,
]

export type PageAdType =
    | 'sidebar'
    | 'sidebar_bottom'
    | 'sidebar_fullscreen'
    | 'sidebar_bottom_fullscreen'
    | 'sticky_desktop'
    | 'sticky_mobile_non_game_page'
    | 'sticky_mobile'
    | 'sticky_tablet'
    | 'leaderboard'
    | 'leaderboard_rest'
    // widget page ad slots
    | 'widget_sidebar'
    | 'widget_sidebar_bottom'
    | 'widget_horizontal'
    | 'widget_horizontal_2'
    | 'widget_sticky_mobile'
    | 'widget_sticky_tablet'
    | 'universal_banner_1'
    | 'universal_banner_2'
    | 'universal_banner_short_1'

    // partner ads sdk formats
    | 'vertical_1'
    | 'vertical_2'
    | 'vertical_3'
    | 'horizontal_1'

export const OutOfPageTypes = [
    'interstitial',
    'interstitial_preroll',
    'rewarded',
    'interstitial_midroll',
    'interstitial_on_return',
] as const

export type OutOfPageAdType = (typeof OutOfPageTypes)[number]

export enum AdProviderStatus {
    loading = 'loading',
    online = 'online',
    offline = 'offline',
}

export type AdType = PageAdType | OutOfPageAdType

export type AdHandlerConfig = Record<AdType, { timeout?: number; initialDelayInMs?: number }>

export type AdUnitConfig = Record<'desktop' | 'mobile', Partial<Record<AdType, string>>>

export type AdUnitProvidersConfig<T extends string> = Record<T, AdUnitConfig>
export type AdProviderTimeoutMsConfig<T extends string> = Record<T, number>

export enum AdvAction {
    preloadRewarded = 'preloadRewarded',
    preloadInterstitial = 'preloadInterstitial',
    showRewarded = 'showRewarded',
    showInterstitial = 'showInterstitial',
    setBannerAdvSettings = 'setBannerAdvSettings',
    adStateChanged = 'adStateChanged',
    showAdvancedBanners = 'showAdvancedBanners',
    hideAdvancedBanners = 'hideAdvancedBanners',
    checkAdBlock = 'checkAdBlock',
}

export type PageAdSize = 'fluid' | [number, number]

export type CreativeRequestOptions = {
    sizes?: PageAdSize[]
    orientation?: string
}

export type PageAdOptions = {
    el: string // id of element where to mount the ad
    sizes: PageAdSize[]
    type: PageAdType
    refresh?: number // seconds
}

export type AdProviderConfig = {
    name?: string
    targeting?: Record<string, string | string[]> & { exp?: string }
    timeout: number
    auid: string
}

export type AdProviderRequestOptions = {
    headerBidding?: HeaderBiddingInterface
    pendingAuction?: PendingHbAuction
    adRootElementId?: string
}

export type Creative = {
    adUnits: Array<AdType>
    format: 'video' | 'banner'
    file: string
    name?: string
    clickUrl?: string
    impressionTrackingUrl?: string
    skippable?: boolean
    width?: number
    height?: number
    responsive?: boolean
    shouldHold?: boolean
    supportedOrientations?: string[]
}

export enum BannerType {
    IN_PAGE = 'IN_PAGE',
    OUT_OF_PAGE = 'OUT_OF_PAGE',
}
